// NEXTSCENES · V1E4C · Strict presentation-only adapter.
import {sceneFirstNovelPresentationV1E5C} from './childrensBookSceneFirstAuthorV1E5C';
// This file produces no UI, no saved data, and never calls Novel's APIs.
// A visual proxy must NEVER become a source of manuscript authority.
const clean = value => String(value ?? '').trim();
const fail = reason => ({ok:false,reason});

export function buildChildrensBookNovelPresentationAdapterV1E4C({
  draft, navigation, readManuscriptText,
}={}) {
  if (draft?.manuscriptPlan?.chapterSceneOutline?.version === 2) {
    return sceneFirstNovelPresentationV1E5C({draft,navigation});
  }
  const units = Array.isArray(draft?.bookMap?.units) ? draft.bookMap.units : [];
  const entries = Array.isArray(draft?.manuscriptPlan?.entries) ? draft.manuscriptPlan.entries : [];
  if (navigation?.ok !== true || !Array.isArray(navigation.sections)) return fail('invalid_projection');
  const unitIds = new Set();
  for (const unit of units) {
    const id = clean(unit?.id);
    if (!id || unitIds.has(id)) return fail('invalid_physical_units');
    unitIds.add(id);
  }
  const byUnit = new Map();
  for (const entry of entries) {
    const id = clean(entry?.unitId);
    if (!id || !unitIds.has(id) || byUnit.has(id)) return fail('invalid_manuscript_entries');
    byUnit.set(id, entry);
  }
  const chapters = navigation.sections.filter(s => s?.kind === 'chapter');
  // V1E4T: a newly saved Children's Book may have zero Chapters.
  // Render the actual Novel Book Workspace without inventing Chapter 1.
  const seenScenes = new Set();
  const seenAssignedUnits = new Set();
  const sceneProxies = [];
  const chapterProxies = [];
  for (const chapter of chapters) {
    const chapterId = clean(chapter?.key);
    if (!chapterId || !Array.isArray(chapter.scenes)) return fail('invalid_chapter');
    const sceneIds = [];
    for (const scene of chapter.scenes) {
      const sceneId = clean(scene?.key);
      if (!sceneId || seenScenes.has(sceneId) || !Array.isArray(scene.entries) || !scene.entries.length) return fail('invalid_scene');
      seenScenes.add(sceneId);
      const unitTexts = [];
      for (const page of scene.entries) {
        const unitId = clean(page?.unitId);
        if (!unitIds.has(unitId) || seenAssignedUnits.has(unitId)) return fail('invalid_scene_unit');
        seenAssignedUnits.add(unitId);
        const entry = byUnit.get(unitId);
        const text = typeof readManuscriptText === 'function'
          ? readManuscriptText(entry || {})
          : (typeof entry?.manuscriptText === 'string' ? entry.manuscriptText : '');
        unitTexts.push(String(text || ''));
      }
      sceneIds.push(sceneId);
      sceneProxies.push({
        _id:sceneId, title:clean(scene?.title)||sceneId,
        text:unitTexts.join('\n\n'),
        // These two fields ONLY satisfy existing Novel preview's scene-display filter.
        // They do not mark a Children's Book Scene adopted, approved, or Canon.
        type:'canon', status:'approved',
      });
    }
    chapterProxies.push({id:chapterId,title:clean(chapter.title)||chapterId,sceneIds});
  }
  // Physical Front/Back Matter is projected for Novel's EXISTING read-only
  // Matter views. Original units remain the only editable/source identities.
  const projectMatter = (kind) => navigation.sections.filter(section=>section?.kind===kind)
    .flatMap(section=>Array.isArray(section.entries)?section.entries:[])
    .map(page=>{
      const unitId=clean(page.unitId);
      if(!unitIds.has(unitId)) return null;
      const entry=byUnit.get(unitId);
      return {
        id:unitId,
        title:clean(page.title)||clean(page.pageLabel)||unitId,
        text:String(typeof readManuscriptText==='function'
          ? readManuscriptText(entry||{})
          : entry?.manuscriptText||''),
        manuscriptContent:entry?.manuscriptContent||null,
      };
    }).filter(Boolean);
  const frontMatter=projectMatter('front');
  const backMatter=projectMatter('back');
  const title=clean(draft?.identity?.title)||'Untitled Children’s Book';
  // The protected Novel preview mode prevents Novel APIs from being called.
  // Production enablement needs a children-only labeling guard; do not claim
  // that this presentation proxy is a saved Novel Story.
  const project={
    id:'childrens-book-presentation-proxy-only',
    projectId:'childrens-book-presentation-proxy-only',
    title,
    isStructurePreview:true,
    childrensBookPresentationBridgeV1E4C:true,
    structurePreviewStory:{
      _id:'childrens-book-presentation-proxy-only',title,
      writingMode:'solo',publicationStatus:'inDevelopment',scenes:sceneProxies,
      frontMatter,backMatter,
    },
    structurePreviewChapters:chapterProxies,
  };
  return {ok:true,project,chapters:chapterProxies,sceneCount:sceneProxies.length,physicalUnitCount:units.length};
}
